/**
 * Сохранённые отчёты ИИ как сущность (agroai-6al.57, ADR-0016): клиент
 * `/ai/agent/saved-reports*` и публичного `/ai/agent/public/reports/{token}`.
 * Отчёт автора и публичный снимок — один и тот же формат `Report`.
 */

import { call } from '../history';
import { isReport, type Cell, type Report } from './types';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';

const m = defineMessages({
  notAReport: { id: 'ai.savedReports.notAReport', defaultMessage: 'сервер вернул не отчёт', description: 'Причина ошибки: ответ сервера не похож на отчёт; строчными' },
  linkInvalid: { id: 'ai.savedReports.linkInvalid', defaultMessage: 'Ссылка недействительна или отозвана автором.', description: 'Ошибка на публичной странице отчёта (гость): ссылка не работает или отозвана' },
  tooMany: { id: 'ai.savedReports.tooMany', defaultMessage: 'Слишком много просмотров, повторите через минуту.', description: 'Ошибка на публичной странице отчёта (гость): превышен лимит просмотров' },
  openFailed: { id: 'ai.savedReports.openFailed', defaultMessage: 'Не удалось открыть отчёт ({status}).', description: 'Ошибка на публичной странице отчёта; {status} — код ответа сервера, например 500' },
  notAReportSentence: { id: 'ai.savedReports.notAReportSentence', defaultMessage: 'Сервер вернул не отчёт.', description: 'Ошибка на публичной странице отчёта: ответ сервера не похож на отчёт' },
  csvTable: { id: 'ai.savedReports.csvTable', defaultMessage: 'Таблица', description: 'Заголовок таблицы в скачанном файле CSV, когда у таблицы нет своего названия' },
});

const BASE = `${(import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '')}/ai/agent`;

export interface SavedReportEntry {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  /** Когда собраны данные снимка (`generated_at` отчёта). */
  data_at: string | null;
  shared: boolean;
  /** Номер текущей версии (agroai-6al.60). */
  version: number;
}

export interface ReportVersion {
  number: number;
  title: string;
  note: string;
  created_at: string;
  current: boolean;
}

export interface SavedReport extends SavedReportEntry {
  share_token: string | null;
  report: Report;
}

const path = (id: string, tail = '') => `/saved-reports/${encodeURIComponent(id)}${tail}`;

function checked(body: SavedReport): SavedReport {
  if (!body || !isReport(body.report)) throw new Error(tr(m.notAReport));
  return body;
}

export const listSavedReports = (q = '') =>
  call<SavedReportEntry[]>(`/saved-reports${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`);

export const getSavedReport = (id: string) => call<SavedReport>(path(id)).then(checked);

export const createSavedReport = (chatId: string, toolCallId: string) =>
  call<SavedReport>('/saved-reports', { method: 'POST', body: JSON.stringify({ chat_id: chatId, tool_call_id: toolCallId }) }).then(checked);

export const refreshSavedReport = (id: string) => call<SavedReport>(path(id, '/refresh'), { method: 'POST' }).then(checked);

export const shareSavedReport = (id: string) => call<SavedReport>(path(id, '/share'), { method: 'POST' }).then(checked);

export const unshareSavedReport = (id: string) => call<SavedReport>(path(id, '/share'), { method: 'DELETE' }).then(checked);

export const listReportVersions = (id: string) => call<ReportVersion[]>(path(id, '/versions'));

/** Откат: содержимое версии становится новой текущей версией. */
export const rollbackSavedReport = (id: string, number: number) =>
  call<SavedReport>(path(id, '/rollback'), { method: 'POST', body: JSON.stringify({ number }) }).then(checked);

/** «Новый на основе этого»: независимая копия. */
export const copySavedReport = (id: string) => call<SavedReport>(path(id, '/copy'), { method: 'POST' }).then(checked);

/** Переименование (только автор). */
export const renameSavedReport = (id: string, title: string) =>
  call<{ id: string; title: string }>(path(id), { method: 'PATCH', body: JSON.stringify({ title }) });

export interface ReportVersionFull extends ReportVersion {
  report: Report;
}

/** Содержимое прежней версии — для переключателя версий. */
export const getReportVersion = (id: string, number: number) => call<ReportVersionFull>(path(id, `/versions/${number}`));

export const deleteSavedReport = (id: string) => call<void>(path(id), { method: 'DELETE' });

/** Публичный снимок по токену: без входа и без cookie. */
export async function fetchPublicReport(token: string): Promise<{ title: string; report: Report }> {
  const response = await fetch(`${BASE}/public/reports/${encodeURIComponent(token)}`, { credentials: 'omit' });
  if (response.status === 404) throw new Error(tr(m.linkInvalid));
  if (response.status === 429) throw new Error(tr(m.tooMany));
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok || !body) throw new Error(tr(m.openFailed, { status: response.status }));
  const out = body as { title: string; report: unknown };
  if (!isReport(out.report)) throw new Error(tr(m.notAReportSentence));
  return { title: out.title, report: out.report };
}

/**
 * Та же ссылка для ВОШЕДШЕГО (журнал №1710): сервер отдаёт снимок и параметры
 * тех виджетов, что лежат в области чтения зрителя, — браузер рисует их живыми,
 * читая данные его сессией. `null` — гость или нет права чтения (401/403):
 * остаётся публичный снимок.
 */
export async function fetchSharedReport(token: string): Promise<{ title: string; report: Report } | null> {
  const response = await fetch(`${BASE}/shared/reports/${encodeURIComponent(token)}`, { credentials: 'same-origin' });
  if (response.status === 401 || response.status === 403) return null;
  if (response.status === 404) throw new Error(tr(m.linkInvalid));
  if (response.status === 429) throw new Error(tr(m.tooMany));
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok || !body) throw new Error(tr(m.openFailed, { status: response.status }));
  const out = body as { title: string; report: unknown };
  if (!isReport(out.report)) throw new Error(tr(m.notAReportSentence));
  return { title: out.title, report: out.report };
}

/** Полный адрес публичной страницы отчёта. */
export const publicUrl = (token: string) => `${window.location.origin}/reports/${token}`;

/** Идентификатор сущности (UUID) отличается от токена ссылки. */
export const isEntityId = (ref: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(ref);

/* ------------------------------------------------------------ скачивание */

const csvCell = (value: Cell): string => {
  const text = value === null || value === undefined ? '' : String(value);
  // Защита от формул в таблицах: ячейка не должна начинаться с =, +, -, @.
  const safe = /^[=+\-@]/.test(text) && typeof value !== 'number' ? `'${text}` : text;
  return /[";\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

/** Блоки-таблицы отчёта как CSV (разделитель «;», UTF-8 с BOM — Excel читает кириллицу). */
export function reportTablesCsv(report: Report): string {
  const parts: string[] = [];
  for (const block of report.blocks) {
    if (block.type !== 'table') continue;
    parts.push(csvCell(block.title ?? tr(m.csvTable)));
    parts.push(block.columns.map((col) => csvCell(col.unit ? `${col.label}, ${col.unit}` : col.label)).join(';'));
    for (const row of block.rows) parts.push(row.map(csvCell).join(';'));
    parts.push('');
  }
  return `﻿${parts.join('\r\n')}`;
}

export const hasTables = (report: Report) => report.blocks.some((block) => block.type === 'table');

export function downloadText(filename: string, text: string, mime = 'text/csv;charset=utf-8'): void {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Параметр адреса «Изменить с ИИ» (страница сохранённого отчёта → панель ИИ). */
export const AI_REPORT_PARAM = 'ai_report';
